import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LewishamCouncilPage() {
  const faqItems = [
    {
      question: 'Does Lewisham\'s GREEN DfT rating block my pothole claim?',
      answer: 'It changes your approach, not your right to claim. GREEN means Lewisham performs above average on network-level condition and spend — but Section 58 of the Highways Act 1980 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Their own data shows B and C roads in RED condition rose from 0% to 7.79% between 2020 and 2024, and U-road RED share more than tripled from 1.7% to 5.86%. A good network rating does not prove every pothole was caught.',
    },
    {
      question: 'What if my pothole was on a residential or unclassified road?',
      answer: 'U-roads make up 327km — 84% of Lewisham\'s 391km carriageway network. At the last survey in 2024, 5.86% were in RED condition — roughly 19km of residential streets. The council surveys all roads every two to three years, so there is no condition data for U-roads in 2021 or 2023. If your incident fell in a blind year, the council cannot point to a contemporaneous network survey for the road type where most pothole claims originate.',
    },
    {
      question: 'Less than half of B and C roads are still Green — does that help my claim?',
      answer: 'It can, if your defect was on a classified road. Lewisham\'s own survey shows B and C roads in Green condition fell from 77.5% in 2020 to 45.98% in 2024, while RED share climbed from 0% to 7.79%. The council defines RED as roads that "should be considered for maintenance." Your claim still needs to tie the specific defect to inspection records and repair timescales — but the trend shows classified roads deteriorating despite a GREEN overall DfT rating.',
    },
    {
      question: 'Lewisham spends more than five times its DfT capital allocation — can I still claim?',
      answer: 'Yes. The DfT Spend scorecard is GREEN — projected capital spend of £3.729m in 2025/26 against a £685,000 DfT allocation, with 79% classed as preventative. That strengthens Lewisham\'s Section 58 defence on paper. But the council still filled 2,947 potholes in 2024/25 and plans around 3,000 minor carriageway repairs in 2025/26. Aggregate spend does not answer whether your specific defect was identified and repaired within a reasonable time.',
    },
    {
      question: 'How does Lewisham\'s two-to-three-year survey cycle affect Section 58?',
      answer: 'The council states it "carries out condition surveys of all roads every two to three years." Condition tables show B/C and U-road data only for 2020, 2022 and 2024 — with blank years in between. To rely on Section 58, Lewisham must show a reasonable system for knowing road condition. A survey gap of up to three years on 327km of U-roads — where RED share has more than tripled — is a structural argument your claim pack can deploy if your incident occurred between surveys.',
    },
    {
      question: 'Lewisham issues Section 58 notices on resurfaced roads — is that the same defence against my claim?',
      answer: 'No. Lewisham issues Section 58 notices under streetworks legislation to protect newly resurfaced roads from utility excavations — flagged in utility companies\' planning systems so only emergency works are permitted. Your pothole claim relies on Section 58 of the Highways Act 1980: whether the council took reasonable care to maintain the specific road. The two provisions share a number, not a defence. The streetworks notices actually show the council understands how repeated digging accelerates surface failure.',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Lewisham Pothole Claims | 84% U-Roads, B/C Green Halved | Fixtyer"
        description="Lewisham Council holds a GREEN DfT rating but its own transparency report shows B and C roads in Green condition falling from 77.5% to 45.98%, U-road RED share tripling, and 13,018 potholes filled in five years. See what this means for your claim."
        canonical="/councils/lewisham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Lewisham', url: '/councils/lewisham' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="Lewisham Council"
        councilSlug="lewisham"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/lewisham"
        headline="Lewisham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Lewisham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Less Than Half of Lewisham's B and C Roads Are Still Green
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            DfT rates Lewisham <strong className="text-emerald-400">GREEN</strong> overall — condition, spend and network averages all above par.
            Yet the council's own survey data tells a sharper story: Green-condition B and C roads fell from{' '}
            <strong className="text-rose-400">77.5% to 45.98%</strong> in four years, U-road RED share more than tripled
            from 1.7% to 5.86% across <strong className="text-amber-400">327km of residential streets</strong>, and the
            borough still filled <strong className="text-rose-400">13,018 potholes in five years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">45.98%</div>
            <div className="text-white font-medium mt-2">B and C roads still in Green condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Down from 77.5% in 2020 — while RED share climbed from 0% to 7.79% on 45km of classified roads.
              A GREEN DfT rating does not mean every road category is holding steady.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The DfT's Verdict — and the AMBER Asterisk
          </h2>
          <p className="text-surface-600 mb-8">
            Lewisham earns GREEN on three of four scorecards. Best Practice is AMBER — and the council is
            transitioning to PAS 2161 condition reporting from 2026/27.
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Lewisham Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">What GREEN Means Here</h3>
              <p className="text-emerald-800 text-sm">
                GREEN does not mean problem-free. It means Lewisham performs above average compared to other
                councils — many of whom are failing outright. The borough projects £3.729m capital spend against
                a £685,000 DfT allocation in 2025/26, classifies 79% of maintenance as preventative, and maintains
                an asset management strategy targeting resources to areas of maximum need.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why Best Practice Is AMBER</h3>
              <p className="text-amber-800 text-sm">
                The council is preparing for a mandatory shift to BSI PAS 2161 condition reporting from 2026/27 —
                five categories instead of three. Condition tables already have gaps: B/C and U-road data exists
                only for 2020, 2022 and 2024, and A-road figures rely on pan-London SCANNER data with 2024 left
                blank. Incomplete, alternate-year reporting on 84% of the network is exactly the kind of gap
                Best Practice scorecards flag.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            327km of Residential Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Lewisham's network is overwhelmingly unclassified — where most pothole claims originate
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Lewisham highway network lengths by road class</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of carriageway</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-surface-100">
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">A-roads</td>
                  <td className="px-6 py-4 text-center">19km</td>
                  <td className="px-6 py-4 text-center">4.9%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">45km</td>
                  <td className="px-6 py-4 text-center">11.5%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">U-roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-bold">327km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">83.6%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-bold">391km</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center">789km</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Segregated cycleways</td>
                  <td className="px-6 py-4 text-center">2.4km</td>
                  <td className="px-6 py-4 text-center">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm mb-6">
            Beyond carriageways, Lewisham maintains approximately 15,800 streetlights, 15,400 gullies, 33 bridges,
            14 footbridges, 4 culverts, 40 retaining walls and 1 subway — all contributing to the maintenance workload
            competing with pothole response.
          </p>

          <QuoteCard
            quote="The Authority carries out condition surveys of all roads every two to three years. The results are in the tables above divided into B & C road (classified roads) and all other roads (unclassified roads)."
            source="Lewisham Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Lewisham's own transparency report — classified roads slipping, U-road RED rising
          </p>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (45km — 11.5% of network): declining fast</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">0%</td>
                    <td className="px-4 py-2 text-center">22.5%</td>
                    <td className="px-4 py-2 text-center">77.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono text-surface-400">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey data</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4.36%</td>
                    <td className="px-4 py-2 text-center">36.98%</td>
                    <td className="px-4 py-2 text-center">58.32%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono text-surface-400">2023</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey data</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.79%</td>
                    <td className="px-4 py-2 text-center font-bold">46.23%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">45.98%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Green-condition B/C roads halved in four years. More than half the classified network (54%) now sits in
              Amber or Red — roads the council defines as needing maintenance "soon" or that "should be considered
              for maintenance."
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">U-roads (327km — 84% of network): RED share tripling</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.7%</td>
                  </tr>
                  <tr className="border-b border-rose-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono text-surface-400">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">No survey data</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.95%</td>
                  </tr>
                  <tr className="border-b border-rose-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono text-surface-400">2023</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic">No survey data</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.86%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              At 5.86% RED, roughly 19km of Lewisham's residential streets were in poor condition at the last survey —
              up from 5.6km in 2020. The council only publishes RED percentages for U-roads, not full Amber/Green splits.
            </p>
          </div>

          {/* A-roads caveat */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
            <h3 className="font-semibold text-brand-900 mb-3">A-roads: pan-London data, incomplete table</h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="text-center bg-white rounded-xl p-4 border border-surface-200">
                <div className="text-sm text-surface-500 mb-1">2022 (19km network)</div>
                <div className="text-rose-600 font-bold">6% Red</div>
                <div className="text-amber-600">28% Amber</div>
                <div className="text-emerald-600">64% Green</div>
              </div>
              <div className="text-center bg-white rounded-xl p-4 border border-surface-200">
                <div className="text-sm text-surface-500 mb-1">2023</div>
                <div className="text-rose-600 font-bold">5% Red</div>
                <div className="text-amber-600">27% Amber</div>
                <div className="text-emerald-600">67% Green</div>
              </div>
            </div>
            <QuoteCard
              quote="In London, the condition surveys and data for principal roads are provided on a pan London basis. This data is from a SCANNER survey. The Borough has principal road data from an AI survey but has not included in this table to maintain consistent information."
              source="Lewisham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            13,018 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a GREEN-rated network — defects still forming between surveys
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Lewisham potholes filled 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,593</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,732</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,741</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,005</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">2,947</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26 (estimate)</td>
                  <td className="px-6 py-4 text-center text-surface-500">2,800</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total (2020/21–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13,018</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~7 Potholes Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Lewisham fills around seven potholes per day — on a compact 391km network
                where 84% is residential. Pothole counts spiked to 3,005 in 2023/24 despite 83% preventative spend that
                year. Defects form between two-to-three-year survey cycles regardless of DfT scorecard colour.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">2025/26 Planned Reactive Work</h3>
              <p className="text-emerald-800 text-sm">
                The council plans around 3,000 minor carriageway repairs and 3,400 minor footway repairs alongside
                resurfacing 5.6km of roads and reconstructing 3.6km of footway. That is a council budgeting for
                thousands of individual defect interventions — not a network where potholes have been eliminated.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Lewisham supplements DfT capital grants heavily from council and TfL sources — and classifies the majority
            of spend as preventative. That is why the Spend scorecard is GREEN.
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6 mb-8 overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Lewisham highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£2,572</td>
                  <td className="px-4 py-3 text-center">£3,140</td>
                  <td className="px-4 py-3 text-center text-emerald-400">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£2,215</td>
                  <td className="px-4 py-3 text-center">£3,210</td>
                  <td className="px-4 py-3 text-center text-emerald-400">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£349</td>
                  <td className="px-4 py-3 text-center">£3,338</td>
                  <td className="px-4 py-3 text-center text-emerald-400">63%</td>
                  <td className="px-4 py-3 text-center">37%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">£211</td>
                  <td className="px-4 py-3 text-center">£3,391</td>
                  <td className="px-4 py-3 text-center">£3,759</td>
                  <td className="px-4 py-3 text-center text-emerald-400">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">£211</td>
                  <td className="px-4 py-3 text-center">£5,068</td>
                  <td className="px-4 py-3 text-center">£3,940</td>
                  <td className="px-4 py-3 text-center text-emerald-400">83%</td>
                  <td className="px-4 py-3 text-center">17%</td>
                </tr>
                <tr className="bg-brand-800/50">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-medium">£685</td>
                  <td className="px-4 py-3 text-center font-medium">£3,729</td>
                  <td className="px-4 py-3 text-center">£4,000</td>
                  <td className="px-4 py-3 text-center text-emerald-400">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div>
              <div className="text-3xl font-bold text-emerald-400">£685k</div>
              <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">£3.729m</div>
              <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">5.4×</div>
              <div className="text-brand-300 text-sm">Capital spend vs DfT grant</div>
            </div>
          </div>

          <QuoteCard
            quote="For the purposes of these figures temporary and small-scale road repairs, street furniture repairs and the winter service are classed as reactive maintenance. Larger road repairs including carriageway patching, drainage work and gully cleaning classed as preventative maintenance."
            source="Lewisham Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Resurfacing */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Resurfacing vs Network Size
          </h2>
          <p className="text-surface-600 mb-8">
            Planned carriageway treatment on a 391km network — the 2022/23 dip stands out
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl border border-surface-200 p-6">
              <h3 className="font-semibold text-brand-900 mb-4">Roads resurfaced (km)</h3>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-surface-200">
                  <tr><td className="py-2">2020/21</td><td className="py-2 text-center font-medium">9.9</td></tr>
                  <tr><td className="py-2">2021/22</td><td className="py-2 text-center font-medium">6.5</td></tr>
                  <tr className="bg-rose-50"><td className="py-2 font-medium">2022/23</td><td className="py-2 text-center font-bold text-rose-600">0.2</td></tr>
                  <tr><td className="py-2">2023/24</td><td className="py-2 text-center font-medium">8.0</td></tr>
                  <tr><td className="py-2">2024/25</td><td className="py-2 text-center font-medium">5.4</td></tr>
                  <tr><td className="py-2">2025/26 (estimate)</td><td className="py-2 text-center font-medium">5.6</td></tr>
                </tbody>
              </table>
              <p className="mt-4 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg">
                Just 0.2km resurfaced in 2022/23 — 0.05% of the network — while pothole fills dropped to 1,741.
                Recovery to 8.0km in 2023/24 coincided with pothole fills jumping to 3,005.
              </p>
            </div>
            <div className="bg-surface-50 rounded-2xl border border-surface-200 p-6">
              <h3 className="font-semibold text-brand-900 mb-4">Footways reconstructed (km)</h3>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-surface-200">
                  <tr><td className="py-2">2020/21</td><td className="py-2 text-center font-medium">0.2</td></tr>
                  <tr><td className="py-2">2021/22</td><td className="py-2 text-center font-medium">5.8</td></tr>
                  <tr><td className="py-2">2022/23</td><td className="py-2 text-center font-medium">0.4</td></tr>
                  <tr><td className="py-2">2023/24</td><td className="py-2 text-center font-medium">3.5</td></tr>
                  <tr><td className="py-2">2024/25</td><td className="py-2 text-center font-medium">5.3</td></tr>
                  <tr><td className="py-2">2025/26 (estimate)</td><td className="py-2 text-center font-medium">3.6</td></tr>
                </tbody>
              </table>
              <p className="mt-4 text-sm text-surface-600">
                On 789km of footways, planned reconstruction covers a fraction each year — 3.6km projected for 2025/26.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Section 58 — Two Different Laws
          </h2>
          <p className="text-surface-600 mb-8">
            Lewisham uses Section 58 notices to protect new surfaces. Your claim turns on Section 58 of the Highways Act 1980.
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="To protect newly resurfaced roads, the Authority also enforces restrictions on excavation by utility companies. Section 58 notices are issued for Authority-led works and serve as formal protection for new road surfaces. These notices are flagged in utility companies' planning systems, ensuring that only emergency works are permitted in affected areas."
              source="Lewisham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">What Section 58 Means For Your Claim</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the <strong className="text-white">Highways Act 1980</strong>, Lewisham can defend
              against pothole damage claims if it proves it had a reasonable maintenance system and did not know of the
              specific defect. On a GREEN-rated borough, expect a documented defence — inspection schedules, intervention
              criteria, repair logs. Your claim succeeds by showing:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• The defect met Lewisham's own intervention thresholds (depth, width, location)</li>
              <li>• It was reported before your incident — or should have been found in routine inspection</li>
              <li>• It persisted beyond reasonable repair timescales on a road surveyed only every two to three years</li>
              <li>• Photographic evidence shows visible age — weathered edges, previous patching, standing water</li>
            </ul>
            <p className="text-white font-medium">
              Network-level GREEN ratings measure averages across 391km. Section 58 turns on the specific defect
              that damaged your vehicle — not the scorecard.
            </p>
          </div>
        </div>
      </section>

      {/* Council admissions / methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Lewisham's Report Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Survey methodology, climate risk, and the coming PAS 2161 transition
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On Condition Categories</h3>
              <QuoteCard
                quote="Red – Should be considered for maintenance"
                source="Lewisham Council Local Highways Maintenance Transparency Report 2025"
              />
              <p className="text-surface-600 mt-3 text-sm">
                When the council classifies 7.79% of B/C roads and 5.86% of U-roads as RED, it is formally recording
                roads that should already be in the maintenance queue — not roads with minor cosmetic defects.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On Methodology Change</h3>
              <QuoteCard
                quote="From 2026/27 a new methodology will be used based on the BSI PAS2161 standard. Local Highway Authorities will be required to use a supplier that has been accredited against PAS2161. This new standard will categorise roads into five categories instead of three to help government gain a more detailed understanding of road condition in England."
                source="Lewisham Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On Climate and Surface Failure</h3>
              <QuoteCard
                quote="Surface water flooding from intense rainfall events is a major concern. Flooding can damage road surfaces, undermine structural integrity, and disrupt traffic flow."
                source="Lewisham Council Local Highways Maintenance Transparency Report 2025"
              />
              <p className="text-surface-600 mt-3 text-sm">
                Lewisham cleans approximately 14,000 gullies per year based on silt-level survey data — acknowledging
                that water damage is an active threat to carriageway integrity, not a theoretical one.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims assessment */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Lewisham is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN condition, spend and overall DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend 5.4× the DfT allocation — serious local investment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 77–83% preventative maintenance share in recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy and London Permit Scheme coordination
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition stable on pan-London SCANNER data (5–6% RED)
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic "the roads are terrible" arguments will fail.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> B/C Green share halved — 77.5% to 45.98% in four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED share more than tripled — 1.7% to 5.86%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 327km of U-roads (84% of network) surveyed only every two to three years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 13,018 potholes filled in five years — defects still forming between surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER Best Practice — PAS 2161 transition and incomplete condition tables
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits surface water flooding "is a major concern" for road integrity
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with three GREEN scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the two-to-three-year survey gap is your strongest structural argument</li>
              <li>• Timing relative to the last condition survey — was your road in a blind year?</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Lewisham's own transparency data where it helps you — without pretending the borough is failing
              when the DfT says otherwise.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Lewisham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-rated borough demands a precise, evidence-led claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No B/C decline data cited</li>
                <li>• No survey-gap argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C Green halved (77.5% → 45.98%) documented</li>
                <li>✅ U-road RED tripling cited from council data</li>
                <li>✅ 13,018 potholes in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Lewisham — honest about GREEN rating</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=lewisham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Lewisham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://lewisham.gov.uk/myservices/roads-and-transport/roads-and-pavements/road-maintenance/highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Lewisham Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
